import React, { forwardRef, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import IntlTelInput from 'react-intl-tel-input';
import 'react-intl-tel-input/dist/main.css';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { selectPortalGatewayISOCodesListToIgnoreAreaCode } from '../../store/selectors/settingsSelectors';
import { shouldIgnoreAreaCodeHelper } from '../../util/helpers/phoneNumberHelpers';
import { InputHeader, InputLabel } from './Components/InputComponents';
import { pxToRem } from '../../assets/styles/helper';
import { variables } from '../../assets/styles/variables';
import themeColors from '../../assets/styles/themeColors';
import Paragraph from '../Paragraph/Paragraph';

const PhoneInputLabel = styled(InputLabel)`
  && {
    font-size: 16px;
    line-height: normal;
    font-weight: 500;
    color: ${themeColors.colorTextPrimary};
    letter-spacing: normal;
  }
`;

const getBorderColors = (isFocused, hasError) => {
  if (isFocused) {
    return themeColors.colorRegistration;
  }

  if (hasError) {
    return themeColors.colorError;
  }

  return themeColors.colorRegistrationInputBorder;
};

const PhoneFieldWrap = styled.div`
  > .intl-tel-input {
    width: 100%;

    .country-list {
      width: 100%;
    }

    input {
      width: 100%;
      height: 54px;
      padding: 12px;
      border-radius: ${variables.borderRadius.borderRadius};
      color: ${({ color }) => color || themeColors.colorLabel};
      font-weight: 400;
      font-size: ${({ fontSize }) =>
        fontSize ?? variables.typography.p.small.fontSize};
      text-transform: ${({ hasSuggestion }) =>
        hasSuggestion ? 'capitalize' : 'none'};
      outline: none;
      border: 1px solid
        ${({ isFocused, hasError }) =>
          `${getBorderColors(isFocused, hasError)}}`};
    }

    .selected-flag {
      border-right: 1px solid
        ${({ isFocused, hasError }) =>
          `${getBorderColors(isFocused, hasError)}}`};
    }
  }
  display: flex;
  flex-direction: column;
`;

const PhoneFieldError = styled.p`
  color: ${themeColors.colorError};
  font-size: 14px;
  font-weight: 500;
  line-height: 1.35;
  margin: ${pxToRem(4)} 0;
`;

const MAX_PHONE_FAIL_ATTEMPTS = 10;

const PhoneField = forwardRef(
  ({
    setValue,
    name,
    label,
    defaultValue,
    isIraAccount,
    setError,
    error,
    optional,
    note,
    color,
    noteFontSize,
    hideErrorOnFocus,
    fontSize,
    dataCy,
  }) => {
    const { t } = useTranslation();
    const [isFocused, setIsFocused] = useState(false);
    const [failCounter, setFailCounter] = useState(0);
    const selectedISOCodesListToIgnoreAreaCode = useSelector(
      selectPortalGatewayISOCodesListToIgnoreAreaCode,
    );
    const intlTelRef = useRef(null);
    const [errorMessage, setErrorMessage] = useState(error);

    useEffect(() => {
      setErrorMessage(error);
    }, [error]);

    useEffect(() => {
      if (defaultValue && intlTelRef.current) {
        const isValid = intlTelRef.current.isValidNumber(defaultValue);
        if (!isValid) {
          setErrorMessage(t('quickRegistration.errors.phone'));
          setError(t('quickRegistration.errors.phone'));
        } else {
          setErrorMessage('');
          setError('');
        }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // process number into string with area code for submission
    const processNumber = (isValid, phone, country) => {
      const shouldIgnoreAreaCode = shouldIgnoreAreaCodeHelper(
        country,
        selectedISOCodesListToIgnoreAreaCode,
      );

      if (shouldIgnoreAreaCode) {
        if (country.iso2 === 'pr') {
          return `+${country.dialCode}${phone}`;
        }

        const phoneNumber = `+${country.dialCode}${phone.substring(3)}`;
        return phoneNumber;
      }

      if (!phone) {
        return '';
      }

      if (phone.includes(`+${country.dialCode}`)) {
        return `${phone}`;
      }

      return `+${country.dialCode}${phone}`;
    };

    return (
      <PhoneFieldWrap
        isFocused={isFocused}
        hasError={errorMessage}
        color={color}
        fontSize={fontSize}
        data-cy={dataCy}
      >
        <InputHeader>
          {label && <PhoneInputLabel>{label}</PhoneInputLabel>}
        </InputHeader>
        <IntlTelInput
          ref={intlTelRef}
          placeholder={isFocused ? '' : undefined}
          onPhoneNumberFocus={() => {
            setIsFocused(true);
            if (hideErrorOnFocus) {
              setErrorMessage('');
            }
          }}
          onPhoneNumberBlur={(isValid, phone) => {
            setIsFocused(false);

            if (!isValid && phone) {
              if (failCounter >= MAX_PHONE_FAIL_ATTEMPTS) {
                setErrorMessage(t('quickRegistration.errors.multipleFails'));
                setError(t('quickRegistration.errors.multipleFails'));
              } else {
                setErrorMessage(t('quickRegistration.errors.phone'));
                setError(t('quickRegistration.errors.phone'));
                setFailCounter((prev) => prev + 1);
              }
            } else if (phone === '') {
              setErrorMessage(
                t('validationSchema.general.phoneNumberRequired'),
              );
              setError(t('validationSchema.general.phoneNumberRequired'));
            } else {
              setErrorMessage('');
              setError('');
            }
          }}
          onPhoneNumberChange={(isValid, phone, country) => {
            setValue(name, processNumber(isValid, phone, country), {
              shouldValidate: true,
            });
          }}
          fieldId={name}
          fieldName={name}
          defaultValue={defaultValue}
          onlyCountries={isIraAccount ? ['us'] : []}
          preferredCountries={isIraAccount ? [] : ['us']}
          defaultCountry="us"
        />
        {errorMessage && <PhoneFieldError>{errorMessage}</PhoneFieldError>}
        {optional && (
          <Paragraph
            marginTop={4}
            fontSize={13}
            color={themeColors.colorTextSecondary}
          >
            {t('quickRegistration.subText.phoneNumber')}
          </Paragraph>
        )}
        {!!note && (
          <Paragraph
            marginTop={4}
            color={themeColors.colorTextSecondary}
            fontWeight={500}
            fontSize={noteFontSize}
            usePx
          >
            {note}
          </Paragraph>
        )}
      </PhoneFieldWrap>
    );
  },
);

PhoneField.propTypes = {
  setValue: PropTypes.func,
  name: PropTypes.string,
  label: PropTypes.string,
  defaultValue: PropTypes.string,
  isIraAccount: PropTypes.bool,
  setError: PropTypes.func,
  optional: PropTypes.bool,
  dataCy: PropTypes.string,
  note: PropTypes.string,
  color: PropTypes.string,
  noteFontSize: PropTypes.number,
  hideErrorOnFocus: PropTypes.bool,
  error: PropTypes.string,
  fontSize: PropTypes.string,
};

export default PhoneField;
